import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotification } from './useNotification';
import { useFetchApi } from './useFetchApi';
import { UserInvitation, UserInvitationStatus } from '../shared/types';
import { useFlags } from '../contexts/FlagsContext';

export const useUserInvitations = (
  status?: UserInvitationStatus | undefined
) => {
  const fetch = useFetchApi();
  const { apiError } = useNotification();
  const queryClient = useQueryClient();
  const { WORKSPACES } = useFlags();

  const enabled = !!status && WORKSPACES;

  const userInvitations = useQuery({
    queryKey: ['user-invitations', status],
    queryFn: () =>
      fetch(`user-invitations?status=${status}`) as Promise<UserInvitation[]>,
    onError: apiError,
    enabled,
    refetchOnWindowFocus: false,
  });

  const updateUserInvitation = useMutation({
    mutationFn: ({
      id,
      update,
    }: {
      id: string;
      update: Partial<UserInvitation>;
    }) =>
      fetch(`user-invitations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(update),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-invitations'] });
    },
  });

  return {
    userInvitations: {
      ...userInvitations,
      isLoading: userInvitations.isLoading && enabled,
    },
    updateUserInvitation,
  };
};
